import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.tsx";
import message from "../components/Message/message.ts";
import { sendForgetPasswordEmail } from "../services/partnerUser.ts";

declare global {
  interface Window {
    lastLoginError?: unknown;
  }
}

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    partnerNumber: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { partnerNumber, email, password } = formData;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isForgotPassword) {
        // 调用忘记密码API
        await sendForgetPasswordEmail({
          partnerNumber,
          email,
        });
        message.success("Reset link sent to your email");
        setIsForgotPassword(false);
      } else {
        await login(partnerNumber, email, password);
        navigate("/dashboard");
      }
    } catch (err: unknown) {
      let msg = isForgotPassword
        ? "Failed to send reset link"
        : "Invalid partner number, email or password";
      if (
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "error" in err.response.data
      ) {
        msg = String(err.response.data.error);
      } else if (err instanceof Error) {
        msg = err.message;
      }
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setIsForgotPassword(true);
    setFormData({ partnerNumber: "", email: "", password: "" });
  };

  const handleBackToLogin = () => {
    setIsForgotPassword(false);
    setFormData({ partnerNumber: "", email: "", password: "" });
  };

  const loginFormFields = [
    {
      name: "partnerNumber",
      label: "Enter partner number",
      type: "text",
      placeholder: "Enter partner number",
      autoComplete: "username",
    },
    {
      name: "email",
      label: "Enter your email",
      type: "email",
      placeholder: "Enter your email",
      autoComplete: "email",
    },
  ];

  const forgotPasswordFields = [
    {
      name: "partnerNumber",
      label: "Partner Number",
      type: "text",
      placeholder: "Enter Partner Number",
      autoComplete: "username",
    },
    {
      name: "email",
      label: "Email address",
      type: "email",
      placeholder: "Enter email address",
      autoComplete: "email",
    },
  ];

  const currentFields = isForgotPassword
    ? forgotPasswordFields
    : loginFormFields;

  const subtitleClassName = isForgotPassword
    ? "text-xl font-bold text-center"
    : "text-lg font-semibold text-center";

  return (
    <div className="min-h-screen bg-gray-50 md:grid md:grid-cols-[42%_58%] md:grid-rows-[1fr_auto]">
      {/* 左侧表单 */}
      <div className="bg-white min-w-0 overflow-y-auto px-4 md:px-8 py-4 flex flex-col items-center justify-center md:row-start-1 md:col-start-1">
        <div className="flex flex-col items-center gap-8 w-full">
          {/* Logo + 标题 */}
          <div className="flex flex-col items-center gap-8">
            {/* <img
              src="/logo_no_background.png"
              alt="Entrio Logo"
              className="w-24 h-24"
            /> */}
            <img
              src="/iqguard-logo.jpg"
              alt="IQGuard"
              className="h-[72px] w-auto object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            <div className="sr-only">IQGuard</div>
            <h1 className="text-xl font-semibold text-gray-900">
              Partner Portal
            </h1>
          </div>

          {/* 副标题 */}
          <h2 className={subtitleClassName}>
            {isForgotPassword ? "Forgot your password?" : "Login to continue"}
          </h2>

          {/* 忘记密码的副标题 */}
          {isForgotPassword && (
            <p className="text-gray-600 text-center">
              We'll get that fixed for you.
            </p>
          )}

          {/* 表单 */}
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-sm flex flex-col items-center"
          >
            {currentFields.map((field) => (
              <div className="w-full mb-3" key={field.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name as keyof typeof formData]}
                  onChange={handleChange}
                  required
                  className="input-field h-12 text-base"
                  placeholder={field.placeholder}
                  autoComplete={field.autoComplete}
                />
              </div>
            ))}

          {/* 密码字段 - 只在登录时显示 */}
          {!isForgotPassword && (
            <div className="w-full mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={handleChange}
                  required
                  className="input-field h-12 text-base pr-12"
                  placeholder="Enter Password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full h-12 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? isForgotPassword
                ? "Sending..."
                : "Logging in..."
              : isForgotPassword
              ? "Send reset link"
              : "Log in"}
          </button>
          </form>
        </div>
      </div>

      {/* 左侧底部：忘记密码/返回登录（与右侧 Need Help 同一行对齐） */}
      <div className="bg-white px-4 md:px-8 pt-2 pb-6 md:row-start-2 md:col-start-1">
        <div className="w-full max-w-sm mx-auto text-center">
          {isForgotPassword ? (
            <button
              onClick={handleBackToLogin}
              className="text-sm text-primary-600 hover:text-primary-700 underline"
            >
              Back to login
            </button>
          ) : (
            <button
              onClick={handleForgotPassword}
              className="text-sm text-primary-600 hover:text-primary-700 underline"
            >
              Forgot your password?
            </button>
          )}
        </div>
      </div>

      {/* 右侧背景（同一块背景：中间欢迎文案 + 底部 Need Help，无额外色块） */}
      <div className="hidden md:flex bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden md:col-start-2 md:row-span-2">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 flex flex-col w-full">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-white px-8">
              <div className="px-10 py-8 max-w-2xl">
                <h2 className="text-2xl font-semibold mb-4">Welcome Back</h2>
                <p className="text-3xl font-bold">IQGuard Partner Portal</p>
                <p className="text-lg opacity-90 mt-4">
                  Your secure gateway to monitor and manage your IQGuard devices.
                </p>
              </div>
            </div>
          </div>

          <div className="w-full flex items-center justify-center px-8 pt-2 pb-6">
            <p className="text-sm opacity-90 text-white">
              Need Help? Contact{" "}
              <a href="mailto:support@iqguard.com" className="underline">
                support@iqguard.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
